import { useEffect, useRef, useState } from 'react';

import { usePostAttachmentsBulkUpload } from '@/api/generated/attachments/attachments';
import type { PostAttachmentsBulkUploadBodyPurpose } from '@/api/generated/lucioAPI.schemas';
import { ANALYTICS_EVENTS } from '@/config/analytics/events';
import { usePostHog } from '@/config/analytics/posthog';
import type { NarrativeUploadFile } from '@/features/narrative/components/NarrativeUploadFileCard';

interface UseNarrativeUploadOptions {
  onFileSelected?: () => void;
  onUploadError?: (error: Error) => void;
  onUploadSuccess?: (
    response: Awaited<ReturnType<ReturnType<typeof usePostAttachmentsBulkUpload>['mutateAsync']>>,
  ) => void;
  /**
   * Optional upload purpose.
   * For profile narrative uploads, this should be "NARRATIVE".
   * For regular narrative page uploads, leave undefined so no purpose key is sent.
   */
  purpose?: PostAttachmentsBulkUploadBodyPurpose;
}

export function useNarrativeUpload(options?: UseNarrativeUploadOptions) {
  const posthog = usePostHog();
  const [uploadFiles, setUploadFiles] = useState<Array<NarrativeUploadFile>>([]);
  const [showUploadDialog, setShowUploadDialog] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const bulkUploadMutation = usePostAttachmentsBulkUpload();

  const handleFileSelection = async (files: Array<File>) => {
    if (!files || files.length === 0) {
      return;
    }

    const selectedFiles = files;
    const uploadFiles: Array<NarrativeUploadFile> = selectedFiles.map((file, index) => ({
      file,
      id: `file-${Date.now()}-${index}`,
      name: file.name,
      status: 'pending' as const,
    }));

    // Call optional callback before showing dialog
    options?.onFileSelected?.();

    // Show upload dialog
    setUploadFiles(uploadFiles);
    setShowUploadDialog(true);

    // Update all files to uploading status
    setUploadFiles((prev) => prev.map((f) => ({ ...f, status: 'uploading' as const })));

    try {
      // Use bulk upload API, optionally with purpose
      const response = await bulkUploadMutation.mutateAsync({
        data: {
          files: selectedFiles,
          ...(options?.purpose ? { purpose: options.purpose } : {}),
        },
      });

      posthog.capture(ANALYTICS_EVENTS.NARRATIVE_SAMPLES_UPLOADED, {
        isRetry: false,
        narrativeSampleCount: response.length,
      });

      // Update all files to completed status
      setUploadFiles((prev) => prev.map((f) => ({ ...f, status: 'completed' as const })));

      // Call success callback with response data
      options?.onUploadSuccess?.(response);
    } catch (error) {
      console.error('Bulk upload failed:', error);

      // Update all files to failed status
      setUploadFiles((prev) => prev.map((f) => ({ ...f, status: 'failed' as const })));

      // Call error callback
      options?.onUploadError?.(error as Error);
    }
  };

  const handleUploadClick = () => {
    // Open file chooser
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      void handleFileSelection(Array.from(files));
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDropFiles = (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    void handleFileSelection(fileArray);
  };

  const handleRetry = async () => {
    // Find all failed files and retry them
    const failedFiles = uploadFiles.filter((f) => f.status === 'failed');
    if (failedFiles.length === 0) {
      return;
    }

    // Reset failed files to pending
    setUploadFiles((prev) =>
      prev.map((f) => (f.status === 'failed' ? { ...f, status: 'pending' as const } : f)),
    );

    // Get File objects from failed files
    const filesToRetry = failedFiles.map((f) => f.file).filter((f): f is File => f !== undefined);

    if (filesToRetry.length === 0) {
      return;
    }

    // Update to uploading status
    setUploadFiles((prev) =>
      prev.map((f) =>
        failedFiles.some((ff) => ff.id === f.id) ? { ...f, status: 'uploading' as const } : f,
      ),
    );

    try {
      // Retry bulk upload, optionally with purpose
      const response = await bulkUploadMutation.mutateAsync({
        data: {
          files: filesToRetry,
          ...(options?.purpose ? { purpose: options.purpose } : {}),
        },
      });

      posthog.capture(ANALYTICS_EVENTS.NARRATIVE_SAMPLES_UPLOADED, {
        isRetry: true,
        narrativeSampleCount: response.length,
      });

      setUploadFiles((prev) => {
        const updated = prev.map((f) =>
          failedFiles.some((ff) => ff.id === f.id) ? { ...f, status: 'completed' as const } : f,
        );
        const allCompleted = updated.every((f) => f.status === 'completed');

        if (allCompleted) {
          options?.onUploadSuccess?.(response);
        }

        return updated;
      });
    } catch (error) {
      console.error('Retry upload failed:', error);

      // Update failed files back to failed status
      setUploadFiles((prev) =>
        prev.map((f) =>
          failedFiles.some((ff) => ff.id === f.id) ? { ...f, status: 'failed' as const } : f,
        ),
      );

      options?.onUploadError?.(error as Error);
    }
  };

  const handleRetryFile = async (fileId: string) => {
    // Find the specific file to retry
    const fileToRetry = uploadFiles.find((f) => f.id === fileId && f.status === 'failed');
    if (!fileToRetry || !fileToRetry.file) {
      return;
    }

    // Reset the file to pending
    setUploadFiles((prev) =>
      prev.map((f) => (f.id === fileId ? { ...f, status: 'pending' as const } : f)),
    );

    // Update to uploading status
    setUploadFiles((prev) =>
      prev.map((f) => (f.id === fileId ? { ...f, status: 'uploading' as const } : f)),
    );

    try {
      // Retry upload for the specific file (use single file array for bulk upload)
      const response = await bulkUploadMutation.mutateAsync({
        data: {
          files: [fileToRetry.file],
          ...(options?.purpose ? { purpose: options.purpose } : {}),
        },
      });

      posthog.capture(ANALYTICS_EVENTS.NARRATIVE_SAMPLES_UPLOADED, {
        isRetry: true,
        narrativeSampleCount: response.length,
      });

      // Update to completed status and check if all files are now completed
      setUploadFiles((prev) => {
        const updated = prev.map((f) =>
          f.id === fileId ? { ...f, status: 'completed' as const } : f,
        );
        const allCompleted = updated.every((f) => f.status === 'completed');

        if (allCompleted) {
          options?.onUploadSuccess?.(response);
        }

        return updated;
      });
    } catch (error) {
      console.error('Retry file upload failed:', error);

      // Update back to failed status
      setUploadFiles((prev) =>
        prev.map((f) => (f.id === fileId ? { ...f, status: 'failed' as const } : f)),
      );

      options?.onUploadError?.(error as Error);
    }
  };

  const handleCancelFile = (fileId: string) => {
    // Remove the file from the upload list
    setUploadFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const handleClose = () => {
    setUploadFiles([]);
  };

  // Auto-close upload dialog when all files have completed successfully
  useEffect(() => {
    if (!showUploadDialog) {
      return;
    }
    if (uploadFiles.length === 0) {
      return;
    }

    const allCompleted = uploadFiles.every((f) => f.status === 'completed');
    if (!allCompleted) {
      return;
    }

    // Small delay so user briefly sees 100% state
    const timer = setTimeout(() => {
      setShowUploadDialog(false);
      setUploadFiles([]);
    }, 600);

    return () => clearTimeout(timer);
  }, [showUploadDialog, uploadFiles]);

  return {
    fileInputRef,
    handleCancelFile,
    handleClose,
    handleDropFiles,
    handleFileInputChange,
    handleRetry,
    handleRetryFile,
    handleUploadClick,
    setShowUploadDialog,
    showUploadDialog,
    uploadFiles,
  };
}
